import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Languages,
  Loader2,
  PauseCircle,
  PlayCircle,
  RotateCcw,
  Sparkles,
  Volume2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import AppLayout from "@/components/AppLayout";
import BilingualText from "@/components/BilingualText";
import Mascot from "@/components/Mascot";
import ProfScalyStage from "@/components/ProfScalyStage";
import { MASCOT } from "@/lib/mascot";
import api from "@/lib/api";
import { cn as cx } from "@/lib/utils";

/**
 * Strip Chinese so TTS only pronounces the English portion when English mode.
 */
function toEnglishOnly(text) {
  if (!text) return "";
  return text
    .replace(/[\u4e00-\u9fff\u3400-\u4dbf\uf900-\ufaff\u3040-\u30ff]+/g, "")
    .replace(/[\u3000-\u303f\uff00-\uffef]+/g, " ")
    .replace(/\s*[/·|]\s*/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function toChineseOnly(text) {
  if (!text) return "";
  return text
    .replace(/[A-Za-z][A-Za-z'’-]*/g, "")
    .replace(/[.,!?;:()"'\-—–]+/g, "")
    .replace(/\s+/g, "")
    .trim();
}

function narrationForSlide(slide, language) {
  if (!slide) return "";
  if (language === "cn") {
    const bits = [];
    if (slide.title_cn) bits.push(slide.title_cn + "。");
    if (slide.chinese) bits.push(slide.chinese);
    for (const ex of slide.examples || []) {
      if (ex.chinese) bits.push(ex.chinese);
    }
    return toChineseOnly(bits.join(" "));
  }
  const bits = [];
  if (slide.title_en) bits.push(slide.title_en + ".");
  if (slide.english) bits.push(slide.english);
  for (const ex of slide.examples || []) {
    if (ex.english) bits.push(ex.english);
  }
  return toEnglishOnly(bits.join(" "));
}

function firstSentence(text) {
  if (!text) return "";
  const m = text.match(/^(.+?[.。!?！？])/);
  return (m ? m[1] : text).trim();
}

/** Convert a raw video URL into an embeddable iframe URL if possible. */
function toEmbedUrl(url, source) {
  if (!url) return null;
  try {
    const u = new URL(url);
    // YouTube — support youtu.be/<id>, youtube.com/watch?v=<id>, youtube.com/shorts/<id>
    if (source === "youtube" || /youtu\.be|youtube\.com/i.test(u.hostname)) {
      let id = "";
      if (u.hostname.includes("youtu.be")) {
        id = u.pathname.replace("/", "");
      } else if (u.pathname.startsWith("/shorts/")) {
        id = u.pathname.split("/")[2];
      } else if (u.pathname.startsWith("/embed/")) {
        id = u.pathname.split("/")[2];
      } else {
        id = u.searchParams.get("v") || "";
      }
      if (id) return `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1&playsinline=1`;
    }
    // HeyGen share links look like https://share.heygen.com/<id> or https://app.heygen.com/share/<id>
    if (source === "heygen" || /heygen\.com/i.test(u.hostname)) {
      // Most HeyGen share URLs are already embeddable via iframe. Add ?embed for safety.
      if (u.hostname === "share.heygen.com") {
        return url; // share.heygen.com pages are already iframe-friendly
      }
      return url;
    }
  } catch {
    /* fall through */
  }
  return null; // not an embed — treat as direct video src
}

function isDirectVideo(url) {
  if (!url) return false;
  return /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(url) || url.startsWith("/api/lesson-videos/");
}

export default function LessonPlayer() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [autoAdvance, setAutoAdvance] = useState(true);
  const [speed, setSpeed] = useState(0.85);
  const [language, setLanguage] = useState("en");
  const audioRef = useRef(null);
  const urlRef = useRef(null);
  const autoPlayedForSlideRef = useRef({});

  useEffect(() => {
    setLoading(true);
    api
      .get(`/lessons/${slug}`)
      .then(({ data }) => setLesson(data))
      .catch(() => toast.error("Lesson not found"))
      .finally(() => setLoading(false));
    return () => {
      if (audioRef.current) audioRef.current.pause();
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, [slug]);

  const slide = lesson?.slides?.[idx];
  const total = lesson?.slides?.length || 0;
  const hasSlides = total > 0;

  const narration = useMemo(() => narrationForSlide(slide, language), [slide, language]);
  const voice = language === "cn" ? "nova" : "fable";
  const currentCaption = useMemo(
    () => firstSentence(language === "cn" ? slide?.chinese : slide?.english),
    [slide, language],
  );

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setPlaying(false);
    }
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
  }, [narration, speed, voice]);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlaying(false);
  }, []);

  const setSpeedAndReset = useCallback((newSpeed) => {
    if (newSpeed === speed) return;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
    setPlaying(false);
    setSpeed(newSpeed);
  }, [speed]);

  const setLanguageAndReset = useCallback((newLang) => {
    if (newLang === language) return;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current);
      urlRef.current = null;
    }
    setPlaying(false);
    setLanguage(newLang);
  }, [language]);

  const goto = useCallback((newIdx) => {
    if (newIdx < 0 || newIdx >= total) return;
    stopAudio();
    setIdx(newIdx);
  }, [stopAudio, total]);

  const play = useCallback(async () => {
    if (!narration) return;
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setPlaying(false);
      return;
    }
    if (urlRef.current && audioRef.current) {
      setPlaying(true);
      await audioRef.current.play().catch(() => setPlaying(false));
      return;
    }
    setPreparing(true);
    try {
      const res = await api.post(
        "/tts",
        { text: narration, voice, speed },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      urlRef.current = url;
      const el = new Audio(url);
      audioRef.current = el;
      el.onended = () => {
        setPlaying(false);
        if (autoAdvance && idx + 1 < total) {
          setTimeout(() => setIdx((i) => Math.min(i + 1, total - 1)), 800);
        }
      };
      el.onpause = () => setPlaying(false);
      setPlaying(true);
      await el.play().catch(() => setPlaying(false));
    } catch {
      toast.error("Audio unavailable. Try again in a moment.");
    } finally { setPreparing(false); }
  }, [narration, speed, autoAdvance, idx, total, voice]);

  useEffect(() => {
    if (!lesson || !narration) return;
    if (autoPlayedForSlideRef.current[`${idx}:${language}`]) return;
    const t = setTimeout(() => {
      autoPlayedForSlideRef.current[`${idx}:${language}`] = true;
      play().catch(() => {});
    }, 600);
    return () => clearTimeout(t);
  }, [lesson?.slug, idx, language, narration, play]);

  const restart = () => { stopAudio(); setIdx(0); };

  if (loading || !lesson) {
    return (
      <AppLayout>
        <div className="grid place-items-center py-24">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </AppLayout>
    );
  }

  const progressPct = hasSlides ? ((idx + 1) / total) * 100 : 0;

  // ---- Video rendering ----
  const videoUrl = lesson.video_url;
  const source = lesson.video_source; // "upload" | "direct" | "youtube" | "heygen" | null
  const embedUrl = videoUrl ? toEmbedUrl(videoUrl, source) : null;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const directVideoSrc = videoUrl && (source === "upload" || isDirectVideo(videoUrl))
    ? (videoUrl.startsWith("http") ? videoUrl : `${backendUrl}${videoUrl}`)
    : null;
  const isEmbed = !!embedUrl;

  return (
    <AppLayout>
      <div data-testid="lesson-player" className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <span className="badge-soft mb-2">
              {lesson.level} · {lesson.grade_hint || lesson.subject || "lesson"}
            </span>
            <BilingualText cn={lesson.title_cn} en={lesson.title_en} size="hero" />
            {hasSlides && (
              <p className="mt-2 text-sm text-slate-500">
                <span className="cn mr-1">{MASCOT.cn} 讲课 ·</span> Narrated by {MASCOT.en}
              </p>
            )}
          </div>
          <Button asChild variant="outline" className="rounded-full font-bold">
            <Link to="/lessons" data-testid="lesson-back-btn">
              <ChevronLeft className="w-4 h-4 mr-1" />
              <span className="cn mr-1">课程列表</span>All lessons
            </Link>
          </Button>
        </div>

        {/* Progress bar (only when there are slides) */}
        {hasSlides && (
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold text-slate-500 shrink-0">
              {idx + 1} / {total}
            </span>
            <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-sky-400 to-emerald-400 transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        )}

        {/* Video / animated stage */}
        {isEmbed ? (
          <div
            className="relative card-plush overflow-hidden bg-black"
            data-testid="lesson-video-stage"
          >
            <div className="aspect-video">
              <iframe
                src={embedUrl}
                title={lesson.title_en}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="w-full h-full"
                data-testid="lesson-video-iframe"
              />
            </div>
          </div>
        ) : directVideoSrc ? (
          <div
            className="relative card-plush overflow-hidden bg-black"
            data-testid="lesson-video-stage"
          >
            <video
              controls
              autoPlay
              playsInline
              className="w-full aspect-video bg-black"
              src={directVideoSrc}
              data-testid="lesson-video-element"
            />
          </div>
        ) : hasSlides ? (
          <ProfScalyStage
            title={{ cn: slide.title_cn, en: slide.title_en }}
            caption={currentCaption}
            language={language}
            playing={playing}
          />
        ) : (
          <div className="card-plush p-10 text-center text-slate-400">
            No video or slides available yet. Ask an admin to upload one.
          </div>
        )}

        {/* Controls — only visible for slide-based lessons */}
        {hasSlides && !isEmbed && !directVideoSrc && (
          <div className="card-plush p-4 sm:p-5 flex items-center justify-center flex-wrap gap-3">
            <Button
              onClick={play}
              size="lg"
              disabled={preparing}
              className={cx(
                "rounded-full font-bold shadow-md",
                playing
                  ? "bg-amber-500 hover:bg-amber-600 text-white"
                  : "bg-slate-900 hover:bg-slate-800 text-white",
              )}
              data-testid="lesson-play-btn"
              data-playing={playing ? "1" : "0"}
            >
              {preparing ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : playing ? (
                <PauseCircle className="w-5 h-5 mr-2" />
              ) : (
                <PlayCircle className="w-5 h-5 mr-2" />
              )}
              <span className="cn mr-1">{playing ? "暂停" : "播放"}</span>
              {playing ? "Pause" : "Play"}
            </Button>

            <div
              className="inline-flex items-center gap-1 p-1 rounded-full bg-slate-100"
              data-testid="lesson-language-toggle"
            >
              <Languages className="w-4 h-4 ml-2 text-slate-500" />
              {[
                { key: "en", cn: "英文", en: "EN" },
                { key: "cn", cn: "中文", en: "CN" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setLanguageAndReset(opt.key)}
                  data-testid={`lesson-lang-${opt.key}`}
                  data-active={language === opt.key ? "1" : "0"}
                  className={cx(
                    "rounded-full px-3 py-1.5 text-xs font-bold transition-colors",
                    language === opt.key
                      ? "bg-slate-900 text-white shadow-sm"
                      : "text-slate-600 hover:text-slate-900",
                  )}
                >
                  <span className="cn mr-1">{opt.cn}</span>· {opt.en}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-1.5 text-xs">
              <Volume2 className="w-3.5 h-3.5 text-slate-500" />
              {[
                { v: 0.7,  cn: "很慢", en: "Slow" },
                { v: 0.85, cn: "正常", en: "Normal" },
                { v: 1.0,  cn: "快",   en: "Fast" },
              ].map((s) => (
                <button
                  key={s.v}
                  type="button"
                  onClick={() => setSpeedAndReset(s.v)}
                  data-testid={`lesson-speed-${s.v}`}
                  data-active={speed === s.v ? "1" : "0"}
                  className={cx(
                    "rounded-full px-2.5 py-1 text-[11px] font-bold border transition-colors",
                    speed === s.v
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white border-slate-200 text-slate-600 hover:border-slate-400",
                  )}
                >
                  {s.en}
                </button>
              ))}
            </div>

            <label className="flex items-center gap-2 text-xs text-slate-500 font-bold cursor-pointer">
              <input
                type="checkbox"
                checked={autoAdvance}
                onChange={(e) => setAutoAdvance(e.target.checked)}
                data-testid="lesson-auto-advance"
                className="accent-emerald-500"
              />
              <span className="cn mr-1">自动</span>Auto-advance
            </label>
          </div>
        )}

        {/* Slide detail card */}
        {hasSlides && (
          <div className="card-plush overflow-hidden bg-gradient-to-br from-white via-sky-50 to-emerald-50">
            <div className="p-6 sm:p-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  className="space-y-5"
                  data-testid={`lesson-slide-${idx}`}
                >
                  <BilingualText cn={slide.title_cn} en={slide.title_en} size="xl" />

                  <div className="bg-white/70 backdrop-blur rounded-2xl p-5 border border-white shadow-sm">
                    <div className="cn text-lg font-bold text-slate-900 leading-relaxed">
                      {slide.chinese}
                    </div>
                    <div className="mt-2 text-slate-700 leading-relaxed">
                      {slide.english}
                    </div>
                  </div>

                  {(slide.examples || []).length > 0 && (
                    <div className="space-y-3">
                      <div className="text-xs font-black text-amber-700 uppercase tracking-widest">
                        <Sparkles className="inline w-3.5 h-3.5 mr-1" />
                        Examples · 例子
                      </div>
                      {slide.examples.map((ex, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.12 + i * 0.08 }}
                          className="rounded-2xl p-4 bg-amber-50/70 border border-amber-100"
                        >
                          <div className="cn text-sm font-bold text-slate-900">{ex.chinese}</div>
                          <div className="text-sm text-slate-700 mt-1">{ex.english}</div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        )}

        {/* Nav — only for slide-based lessons */}
        {hasSlides && (
          <div className="flex items-center justify-between gap-3">
            <Button
              variant="outline"
              className="rounded-full font-bold"
              onClick={() => goto(idx - 1)}
              disabled={idx === 0}
              data-testid="lesson-prev-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              <span className="cn mr-1">上一张</span>Previous
            </Button>

            <div className="flex items-center gap-1.5">
              {Array.from({ length: total }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => goto(i)}
                  data-testid={`lesson-dot-${i}`}
                  className={cx(
                    "h-2.5 rounded-full transition-all",
                    i === idx ? "w-8 bg-slate-900" : "w-2.5 bg-slate-200 hover:bg-slate-400",
                  )}
                  aria-label={`Slide ${i + 1}`}
                />
              ))}
            </div>

            {idx + 1 >= total ? (
              <Button
                className="rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold"
                onClick={restart}
                data-testid="lesson-restart-btn"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                <span className="cn mr-1">再听一次</span>Play again
              </Button>
            ) : (
              <Button
                className="rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold"
                onClick={() => goto(idx + 1)}
                data-testid="lesson-next-btn"
              >
                <span className="cn mr-1">下一张</span>Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        )}

        {/* Description block for video-only lessons */}
        {!hasSlides && (lesson.description_cn || lesson.description_en) && (
          <div className="card-plush p-6 sm:p-8">
            {lesson.description_cn && (
              <p className="cn text-lg font-bold text-slate-900 leading-relaxed">
                {lesson.description_cn}
              </p>
            )}
            {lesson.description_en && (
              <p className="mt-2 text-slate-700 leading-relaxed">{lesson.description_en}</p>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
